<template>
  <page>
    <div class="page-title">投放管理</div>

    <div class="page-content">
      <div class="toolbar">
        <el-button type="primary" size="small" @click="openCreate">新增渠道</el-button>
      </div>

      <el-table v-loading="loading" :data="list" border stripe size="small" style="width: 100%">
        <el-table-column :resizable="false" type="index" label="#" width="50" align="center" />
        <el-table-column :resizable="false" prop="channel_no" label="渠道编号" min-width="120" show-overflow-tooltip />
        <el-table-column
          :resizable="false"
          prop="channel_name"
          label="渠道名称"
          min-width="140"
          show-overflow-tooltip
        />
        <el-table-column :resizable="false" prop="channel_type" label="广告渠道" width="120" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" :type="scope.row.channel_type === 'META_PIXEL' ? 'warning' : 'info'">
              {{ channelTypeLabel(scope.row.channel_type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column :resizable="false" label="落地页 URL" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ displayLandingUrl(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column :resizable="false" prop="created_at" label="创建时间" width="168">
          <template slot-scope="scope">{{ formatTs(scope.row.created_at) }}</template>
        </el-table-column>
        <el-table-column :resizable="false" label="操作" width="180" fixed="right" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="openEdit(scope.row)">编辑</el-button>
            <el-popconfirm title="确定删除该渠道？" @confirm="handleDelete(scope.row)">
              <el-button slot="reference" type="text" size="small" style="color: #f56c6c">删除</el-button>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :page-sizes="[10, 20, 30, 50]"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="560px" destroy-on-close @closed="resetForm">
      <el-form
        ref="formRef"
        v-loading="detailLoading"
        :model="form"
        :rules="formRules"
        label-width="108px"
        size="small"
      >
        <el-form-item label="渠道编号" prop="channel_no">
          <el-input
            v-model="form.channel_no"
            placeholder="唯一编号，仅支持字母、数字、下划线，如 meta_01"
            :disabled="isEdit"
            clearable
          />
        </el-form-item>
        <el-form-item label="渠道名称" prop="channel_name">
          <el-input v-model="form.channel_name" placeholder="展示名称" clearable />
        </el-form-item>
        <el-form-item label="广告渠道" prop="channel_type">
          <el-select v-model="form.channel_type" placeholder="请选择" style="width: 100%" @change="onChannelTypeChange">
            <el-option label="META_PIXEL" value="META_PIXEL" />
            <el-option label="LINK" value="LINK" />
          </el-select>
        </el-form-item>

        <template>
          <el-form-item label="落地页 URL" prop="url">
            <div>
              {{ form.url }}
              <el-button
                type="text"
                icon="el-icon-copy-document"
                border="none"
                size="mini"
                class="copy-btn"
                @click="copyText(form.url)"
              ></el-button>
            </div>
          </el-form-item>
        </template>

        <template v-if="form.channel_type === 'META_PIXEL'">
          <el-form-item label="Pixel ID" prop="pixel_id">
            <el-input v-model="form.pixel_id" placeholder="pixel_id" clearable />
          </el-form-item>
          <el-form-item label="Access Token" prop="access_token">
            <el-input v-model="form.access_token" type="textarea" :rows="3" placeholder="access_token" clearable />
          </el-form-item>
          <el-form-item label="Meta 配置">
            <el-input
              :value="metaConfigJsonPreview"
              type="textarea"
              :rows="6"
              readonly
              class="meta-config-json"
              placeholder="提交时将序列化为 channel_config"
            />
          </el-form-item>
        </template>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="submitting" @click="submitForm">{{
          submitButtonText
        }}</el-button>
      </div>
    </el-dialog>
  </page>
</template>

<script>
import {
  getAdsChannelList,
  getAdsChannelDetail,
  createAdsChannel,
  updateAdsChannel,
  deleteAdsChannel,
} from '@/api/operation/adsChannel'
import { formatTimestamp } from '@/utils/time'

const CHANNEL_TYPES = {
  META_PIXEL: 'META_PIXEL',
  LINK: 'LINK',
}

const LINK_LANDING_BASE_MAP = {
  'localhost:9527': 'https://brl.demo.flashgameapp.com',
  'demo.flashgame.admin.s3-website.ap-east-1.amazonaws.com': 'https://brl.demo.flashgameapp.com',
  'apgame.admin.sorte44.com': 'https://sorte44.com',
}
/** 落地页默认基础地址，拼接 ?ads_channel={channel_no}（LINK / META_PIXEL 共用） */
const LANDING_BASE_DEFAULT = 'https://brl.demo.flashgameapp.com'

/** 渠道编号：仅数字、字母、下划线 */
const CHANNEL_NO_PATTERN = /^[a-zA-Z0-9_]+$/

export default {
  name: 'AdsPlacementManage',
  data() {
    return {
      loading: false,
      list: [],
      page: 1,
      pageSize: 20,
      total: 0,
      dialogVisible: false,
      isEdit: false,
      editId: null,
      submitting: false,
      detailLoading: false,
      form: {
        channel_no: '',
        channel_name: '',
        channel_type: CHANNEL_TYPES.LINK,
        url: '',
        pixel_id: '',
        access_token: '',
      },
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑渠道' : '新增渠道'
    },
    submitButtonText() {
      return this.isEdit ? '保存' : '创建'
    },
    /** 与提交体 channel_config 一致的 JSON 预览（META_PIXEL） */
    metaConfigJsonPreview() {
      if (this.form.channel_type !== CHANNEL_TYPES.META_PIXEL) return ''
      try {
        return JSON.stringify(
          {
            pixel_id: (this.form.pixel_id || '').trim(),
            access_token: (this.form.access_token || '').trim(),
          },
          null,
          2
        )
      } catch (e) {
        return ''
      }
    },
    landingUrlRules() {
      return [
        {
          validator: (_rule, _value, callback) => {
            const no = (this.form.channel_no || '').trim()
            if (!no) {
              callback(new Error('请先填写渠道编号'))
            } else if (!CHANNEL_NO_PATTERN.test(no)) {
              callback(new Error('渠道编号仅允许数字、字母、下划线'))
            } else {
              callback()
            }
          },
          trigger: 'blur',
        },
      ]
    },
    channelNoRules() {
      return [
        { required: true, message: '请输入渠道编号', trigger: 'blur' },
        {
          pattern: CHANNEL_NO_PATTERN,
          message: '渠道编号仅允许数字、字母、下划线',
          trigger: ['blur', 'change'],
        },
      ]
    },
    formRules() {
      const base = {
        channel_no: this.channelNoRules,
        channel_name: [{ required: true, message: '请输入渠道名称', trigger: 'blur' }],
        channel_type: [{ required: true, message: '请选择广告渠道', trigger: 'change' }],
        url: this.landingUrlRules,
      }
      if (this.form.channel_type === CHANNEL_TYPES.META_PIXEL) {
        return {
          ...base,
          pixel_id: [{ required: true, message: '请输入 Pixel ID', trigger: 'blur' }],
          access_token: [{ required: true, message: '请输入 Access Token', trigger: 'blur' }],
        }
      }
      return base
    },
  },
  watch: {
    'form.channel_type'() {
      this.syncLandingUrl()
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    'form.channel_no'() {
      this.syncLandingUrl()
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.validateField('url'))
    },
  },
  created() {
    this.loadList()
  },
  methods: {
    async copyText(text) {
      const t = (text ?? '').toString()
      if (!t) return this.$message?.warning('暂无可复制内容')
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(t)
        } else {
          const el = document.createElement('textarea')
          el.value = t
          el.setAttribute('readonly', 'readonly')
          el.style.position = 'fixed'
          el.style.left = '-9999px'
          document.body.appendChild(el)
          el.select()
          document.execCommand('copy')
          document.body.removeChild(el)
        }
        this.$message?.success('已复制')
      } catch (e) {
        this.$message?.error('复制失败')
      }
    },
    formatTs(ts) {
      if (ts == null || ts === 0) return '—'
      return formatTimestamp(ts)
    },
    channelTypeLabel(t) {
      if (t === 'META_PIXEL') return 'META_PIXEL'
      if (t === 'LINK') return 'LINK'
      return t || '—'
    },
    displayLandingUrl(row) {
      return this.buildLandingUrl(row.channel_no) || '—'
    },
    buildLandingUrl(channelNo) {
      const host = window.location.host
      const base = LINK_LANDING_BASE_MAP[host] || LANDING_BASE_DEFAULT
      const no = (channelNo || '').trim()
      if (!no) {
        return `${base}?ads_channel=`
      }
      return `${base}?ads_channel=${no}`
    },
    syncLandingUrl() {
      this.form.url = this.buildLandingUrl(this.form.channel_no)
    },
    onChannelTypeChange() {
      this.syncLandingUrl()
      this.form.pixel_id = ''
      this.form.access_token = ''
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    resetForm() {
      this.form = {
        channel_no: '',
        channel_name: '',
        channel_type: CHANNEL_TYPES.LINK,
        url: '',
        pixel_id: '',
        access_token: '',
      }
      this.editId = null
      this.isEdit = false
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    openCreate() {
      this.resetForm()
      this.isEdit = false
      this.dialogVisible = true
      this.$nextTick(() => this.syncLandingUrl())
    },
    async openEdit(row) {
      this.isEdit = true
      this.editId = row.id
      this.dialogVisible = true
      this.detailLoading = true
      try {
        const res = await getAdsChannelDetail({ id: row.id })
        const d = res.data || {}
        this.form.channel_no = d.channel_no || ''
        this.form.channel_name = d.channel_name || ''
        this.form.channel_type = d.channel_type === 'META_PIXEL' ? CHANNEL_TYPES.META_PIXEL : CHANNEL_TYPES.LINK
        this.form.url = this.buildLandingUrl(this.form.channel_no)
        this.form.pixel_id = ''
        this.form.access_token = ''
        if (this.form.channel_type === CHANNEL_TYPES.META_PIXEL && d.channel_config) {
          try {
            const c = typeof d.channel_config === 'string' ? JSON.parse(d.channel_config) : d.channel_config
            this.form.pixel_id = c.pixel_id || c.PixelID || ''
            this.form.access_token = c.access_token || c.AccessToken || ''
          } catch (e) {
            /* ignore */
          }
        }
      } catch (e) {
        this.dialogVisible = false
      } finally {
        this.detailLoading = false
      }
    },
    buildPayload() {
      const { channel_type } = this.form
      let channel_config = ''
      if (channel_type === CHANNEL_TYPES.META_PIXEL) {
        channel_config = JSON.stringify({
          pixel_id: this.form.pixel_id.trim(),
          access_token: this.form.access_token.trim(),
        })
      }
      const channelNo = this.form.channel_no.trim()
      const url = this.buildLandingUrl(channelNo)
      return {
        channel_no: this.form.channel_no.trim(),
        channel_name: this.form.channel_name.trim(),
        channel_type,
        url,
        channel_config,
      }
    },
    submitForm() {
      this.$refs.formRef.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          const payload = this.buildPayload()
          if (this.isEdit) {
            await updateAdsChannel({
              id: this.editId,
              ...payload,
            })
            this.$message.success('更新成功')
          } else {
            await createAdsChannel(payload)
            this.$message.success('创建成功')
          }
          this.dialogVisible = false
          this.loadList()
        } catch (e) {
          /* message handled by request */
        } finally {
          this.submitting = false
        }
      })
    },
    async handleDelete(row) {
      try {
        await deleteAdsChannel({ id: row.id })
        this.$message.success('已删除')
        this.loadList()
      } catch (e) {
        /* handled */
      }
    },
    async loadList() {
      this.loading = true
      try {
        const res = await getAdsChannelList({
          page: this.page,
          page_size: this.pageSize,
        })
        const data = res.data || {}
        this.list = data.items || []
        this.total = data.total != null ? data.total : 0
      } catch (e) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.page = 1
      this.loadList()
    },
    handlePageChange(p) {
      this.page = p
      this.loadList()
    },
  },
}
</script>

<style scoped>
.toolbar {
  margin-bottom: 12px;
}
.pagination-wrap {
  margin-top: 16px;
  text-align: right;
}

.meta-config-json >>> textarea {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
}

.link-url-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}
</style>
